import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { FullSurveyData, Ethnicity, RoutineFrequency, SkinColor } from '@/types/fullSurvey'
import { DEFAULT_FULL_SURVEY_LEGACY } from '@/types/fullSurvey'

interface FullSurveyState {
  // Survey data
  fullSurvey: FullSurveyData
  currentStep: number
  totalSteps: number

  // Actions
  setEthnicity: (ethnicity: Ethnicity) => void
  setZipCode: (zipCode: string) => void
  setRoutineFrequency: (frequency: RoutineFrequency) => void
  setSkinColor: (color: SkinColor) => void

  // Navigation
  nextStep: () => void
  prevStep: () => void
  goToStep: (step: number) => void

  // Validation
  isCurrentStepValid: () => boolean
  isComplete: () => boolean

  // Reset
  resetFullSurvey: () => void
}

const initialFullSurveyData: FullSurveyData = {
  ethnicity: null,
  zipCode: null,
  routineFrequency: null,
  skinColor: null,
  // Include legacy fields with defaults for recommendation engine
  ...DEFAULT_FULL_SURVEY_LEGACY,
}

// Validate US ZIP code (5 digits or 5+4 format)
function isValidZipCode(zip: string | null): boolean {
  if (!zip) return false
  const zipRegex = /^\d{5}(-\d{4})?$/
  return zipRegex.test(zip.trim())
}

export const useFullSurveyStore = create<FullSurveyState>()(
  persist(
    (set, get) => ({
      fullSurvey: initialFullSurveyData,
      currentStep: 0,
      totalSteps: 4,

      setEthnicity: (ethnicity) =>
        set((state) => ({
          fullSurvey: { ...state.fullSurvey, ethnicity },
        })),

      setZipCode: (zipCode) =>
        set((state) => ({
          fullSurvey: { ...state.fullSurvey, zipCode },
        })),

      setRoutineFrequency: (routineFrequency) =>
        set((state) => ({
          fullSurvey: { ...state.fullSurvey, routineFrequency },
        })),

      setSkinColor: (skinColor) =>
        set((state) => ({
          fullSurvey: { ...state.fullSurvey, skinColor },
        })),

      nextStep: () =>
        set((state) => ({
          currentStep: Math.min(state.currentStep + 1, state.totalSteps - 1),
        })),

      prevStep: () =>
        set((state) => ({
          currentStep: Math.max(state.currentStep - 1, 0),
        })),

      goToStep: (step) =>
        set((state) => ({
          currentStep: Math.max(0, Math.min(step, state.totalSteps - 1)),
        })),

      isCurrentStepValid: () => {
        const { fullSurvey, currentStep } = get()
        switch (currentStep) {
          case 0: // Ethnicity
            return fullSurvey.ethnicity !== null
          case 1: // ZIP Code
            return isValidZipCode(fullSurvey.zipCode)
          case 2: // Routine Frequency
            return fullSurvey.routineFrequency !== null
          case 3: // Skin Color
            return fullSurvey.skinColor !== null
          default:
            return false
        }
      },

      isComplete: () => {
        const { fullSurvey } = get()
        return (
          fullSurvey.ethnicity !== null &&
          isValidZipCode(fullSurvey.zipCode) &&
          fullSurvey.routineFrequency !== null &&
          fullSurvey.skinColor !== null
        )
      },

      resetFullSurvey: () =>
        set({
          fullSurvey: initialFullSurveyData,
          currentStep: 0,
        }),
    }),
    {
      name: 'k-skin-full-survey',
      partialize: (state) => ({
        fullSurvey: state.fullSurvey,
        currentStep: state.currentStep,
      }),
    }
  )
)
